// Cross-session progress tracking stored in localStorage. No backend required.
// When Supabase is configured, attempts also sync to power a real percentile.
// All reads are guarded for server-side rendering.

import { getSupabase } from "@/lib/atsa/supabase";

export type AttemptRecord = {
  id: string;
  section: string;        // display name, e.g. "Radar Simulation"
  sectionSlug: string;    // e.g. "radar-simulation"
  score: number;          // normalized primary, 0-100
  scoreLabel: string;     // e.g. "Removal efficiency" or "Accuracy"
  mode?: string;
  at: number;             // epoch ms
  detail?: Record<string, string | number>;
};

const KEY = "atsa-progress-v1";
const MAX_RECORDS = 600;

export function loadAttempts(): AttemptRecord[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AttemptRecord[]) : [];
  } catch {
    return [];
  }
}

export function logAttempt(rec: Omit<AttemptRecord, "id" | "at"> & { at?: number }): void {
  if (typeof window === "undefined") return;
  try {
    const records = loadAttempts();
    const entry: AttemptRecord = {
      ...rec,
      id: `${rec.sectionSlug}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      at: rec.at ?? Date.now(),
      score: Math.max(0, Math.min(100, Math.round(rec.score))),
    };
    records.push(entry);
    const trimmed = records.slice(-MAX_RECORDS);
    window.localStorage.setItem(KEY, JSON.stringify(trimmed));
    syncAttemptRemote({ sectionSlug: entry.sectionSlug, score: entry.score });
  } catch {
    /* storage unavailable or full */
  }
}

export function clearAttempts(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

export type SectionSummary = {
  sectionSlug: string;
  section: string;
  scoreLabel: string;
  count: number;
  recent: number;       // most recent score
  best: number;
  average: number;      // mean of all attempts
  recentAverage: number; // mean of last 5
  trend: number;        // recentAverage minus average of the prior window
  lastAt: number;
  scores: number[];     // chronological, for a sparkline
};

function mean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

export function summarize(records: AttemptRecord[] = loadAttempts()): SectionSummary[] {
  const bySection = new Map<string, AttemptRecord[]>();
  for (const record of records) {
    const list = bySection.get(record.sectionSlug) ?? [];
    list.push(record);
    bySection.set(record.sectionSlug, list);
  }

  const summaries: SectionSummary[] = [];
  for (const [slug, listRaw] of bySection) {
    const list = [...listRaw].sort((a, b) => a.at - b.at);
    const last = list[list.length - 1];
    if (!last) continue;
    const scores = list.map((r) => r.score);
    const last5 = scores.slice(-5);
    const prior = scores.slice(-10, -5);
    summaries.push({
      sectionSlug: slug,
      section: last.section,
      scoreLabel: last.scoreLabel,
      count: list.length,
      recent: last.score,
      best: Math.max(...scores),
      average: Math.round(mean(scores)),
      recentAverage: Math.round(mean(last5)),
      trend: Math.round(mean(last5) - mean(prior.length ? prior : last5)),
      lastAt: last.at,
      scores,
    });
  }
  return summaries.sort((a, b) => b.lastAt - a.lastAt);
}

// ---------------------------------------------------------------------------
// Illustrative reference curve for an opt-in, self-comparison percentile.
// IMPORTANT: these are not real candidate results. They are a fixed reference
// distribution for gauging your own scores. A true percentile against other
// users would require a backend that aggregates real attempts.
// ---------------------------------------------------------------------------
const REFERENCE: Record<string, { mean: number; sd: number }> = {
  "memory-differences": { mean: 70, sd: 16 },
  "value-assignments": { mean: 68, sd: 17 },
  "spatial-awareness": { mean: 72, sd: 15 },
  "radar-simulation": { mean: 65, sd: 18 },
  "reading-comprehension": { mean: 74, sd: 14 },
  "logical-reasoning": { mean: 66, sd: 17 },
};

function normalCdf(z: number): number {
  // Abramowitz and Stegun approximation of the standard normal CDF.
  const t = 1 / (1 + 0.2316419 * Math.abs(z));
  const d = 0.3989423 * Math.exp((-z * z) / 2);
  let p = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
  p = 1 - p;
  return z >= 0 ? p : 1 - p;
}

export function referencePercentile(sectionSlug: string, score: number): number | null {
  const ref = REFERENCE[sectionSlug];
  if (!ref) return null;
  const z = (score - ref.mean) / ref.sd;
  return Math.max(1, Math.min(99, Math.round(normalCdf(z) * 100)));
}

// ---------------------------------------------------------------------------
// Optional Supabase sync. A stable anonymous id lets the same browser's attempts
// be grouped without requiring sign-in. Inserts are best-effort and never block
// or throw into the UI.
// ---------------------------------------------------------------------------
const CLIENT_ID_KEY = "atsa-client-id";

function clientId(): string {
  if (typeof window === "undefined") return "server";
  try {
    let id = window.localStorage.getItem(CLIENT_ID_KEY);
    if (!id) {
      id = (window.crypto?.randomUUID?.() ?? `c-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      window.localStorage.setItem(CLIENT_ID_KEY, id);
    }
    return id;
  } catch {
    return "anon";
  }
}

export function syncAttemptRemote(rec: { sectionSlug: string; score: number }): void {
  const supabase = getSupabase();
  if (!supabase) return;
  void supabase
    .from("atsa_practice_attempts")
    .insert({ section: rec.sectionSlug, score: Math.round(rec.score), client_id: clientId() })
    .then(
      () => undefined,
      () => undefined,
    );
}

// Real percentile across all users of this site, via a SECURITY DEFINER RPC.
// Returns null when Supabase is not configured or the sample is too small.
export async function getRemotePercentile(sectionSlug: string, score: number): Promise<number | null> {
  const supabase = getSupabase();
  if (!supabase) return null;
  try {
    const { data, error } = await supabase.rpc("atsa_section_percentile", {
      p_section: sectionSlug,
      p_score: Math.round(score),
    });
    if (error || data === null || data === undefined) return null;
    const value = Number(data);
    return Number.isFinite(value) ? Math.max(0, Math.min(100, Math.round(value))) : null;
  } catch {
    return null;
  }
}
